/**
 * Content Engine API client — thin wrapper over fetch + sip_token auth.
 * Handles the 402 UPGRADE_REQUIRED response shape uniformly.
 */
const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const BASE = `${API_URL}/api/content-engine`;

class UpgradeRequiredError extends Error {
  constructor(detail) {
    super(detail?.message || 'Upgrade required');
    this.code = 'UPGRADE_REQUIRED';
    this.detail = detail || {};
  }
}

// Parse filename from Content-Disposition (falls back to provided name)
function _filenameFromResponse(res, fallback) {
  const cd = res.headers.get('Content-Disposition') || '';
  const m = cd.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return (m && decodeURIComponent(m[1])) || fallback;
}

// Save a blob from an authenticated fetch Response to disk
async function _saveBlobFromResponse(res, fallbackFilename) {
  const blob = await res.blob();
  const filename = _filenameFromResponse(res, fallbackFilename);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Delay revocation so the browser can actually start the download
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  return { filename, size: blob.size };
}

const headers = () => {
  const token = localStorage.getItem('sip_token');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
};

async function request(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: headers(),
    ...opts,
  });
  if (res.status === 402) {
    const body = await res.json().catch(() => ({}));
    throw new UpgradeRequiredError(body.detail || body);
  }
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const msg = body.detail || body.message || `Request failed: ${res.status}`;
    throw new Error(typeof msg === 'string' ? msg : JSON.stringify(msg));
  }
  return res.json();
}

export const contentEngineApi = {
  UpgradeRequiredError,

  // Curriculum
  listBoards: () => request('/curriculum/boards'),
  listChapters: (board, classId, subject) =>
    request(`/curriculum/chapters?board=${encodeURIComponent(board)}&classId=${encodeURIComponent(classId)}&subject=${encodeURIComponent(subject)}`),
  getSyllabus: (board, classId, subject) =>
    request(`/curriculum/syllabus?board=${encodeURIComponent(board)}&classId=${encodeURIComponent(classId)}&subject=${encodeURIComponent(subject)}`),

  // Books library
  listBooks: (board, classId, subject) => {
    const qs = new URLSearchParams();
    if (board) qs.set('board', board);
    if (classId) qs.set('classId', classId);
    if (subject) qs.set('subject', subject);
    const q = qs.toString();
    return request(`/books${q ? `?${q}` : ''}`);
  },
  getBook: (id) => request(`/books/${id}`),
  uploadBook: async (file, { board, classId, subject, title } = {}) => {
    const token = localStorage.getItem('sip_token');
    const form = new FormData();
    form.append('file', file);
    if (board) form.append('board', board);
    if (classId) form.append('classId', classId);
    if (subject) form.append('subject', subject);
    if (title) form.append('title', title);
    const res = await fetch(`${BASE}/books/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (res.status === 402) {
      const body = await res.json().catch(() => ({}));
      throw new UpgradeRequiredError(body.detail || body);
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || body.message || `Upload failed: ${res.status}`);
    }
    return res.json();
  },

  // Doc types (for tier-based caps in UI)
  listDocTypes: () => request('/doc-types'),

  // ── Multi-document batch generation (ASYNC, iter 67) ──
  // Kicks off a background job, then polls for completion. Bypasses the
  // 60 s ingress upstream timeout entirely → unlimited chapters / sources.
  // `onProgress` is invoked with {status, elapsedMs} after every poll.
  generateMulti: async (payload, { onProgress, signal, pollIntervalMs = 2500, maxWaitMs = 15 * 60 * 1000 } = {}) => {
    // 1) Start the job (returns 202 + jobId in <1 s)
    const start = await request('/generate/multi/async', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    const jobId = start.jobId;
    if (!jobId) throw new Error('Async generation did not return a jobId.');

    // 2) Poll job status until done / failed / timed out
    const startedAt = Date.now();
    let lastStatus = start.status || 'queued';
    if (typeof onProgress === 'function') onProgress({ status: lastStatus, jobId, elapsedMs: 0 });

    while (true) {
      if (signal?.aborted) {
        const err = new Error('Generation cancelled');
        err.code = 'CANCELLED';
        throw err;
      }
      await new Promise((r) => setTimeout(r, pollIntervalMs));
      const job = await request(`/generate/job/${encodeURIComponent(jobId)}`);
      lastStatus = job.status;
      const elapsedMs = Date.now() - startedAt;
      if (typeof onProgress === 'function') onProgress({ status: lastStatus, jobId, elapsedMs });
      if (lastStatus === 'done') return job.result;
      if (lastStatus === 'failed') {
        const detail = job.error;
        if (job.errorCode === 402 && detail && typeof detail === 'object') {
          throw new UpgradeRequiredError(detail);
        }
        const msg = typeof detail === 'string' ? detail : detail?.message || 'Generation failed.';
        const err = new Error(msg);
        err.detail = detail;
        err.code = (detail && detail.code) || 'GENERATION_FAILED';
        throw err;
      }
      if (elapsedMs > maxWaitMs) {
        throw new Error(`Generation timed out after ${Math.round(maxWaitMs / 1000)} s. Try fewer chapters or contact support.`);
      }
    }
  },

  // Legacy sync endpoint — kept for tests/scripts that POST directly.
  generateMultiSync: (payload) =>
    request('/generate/multi', { method: 'POST', body: JSON.stringify(payload) }),

  // OPTIONAL Blueprint Layer — Pro/Elite/School only. Falls back silently
  // server-side if anything goes wrong, so generation never blocks.
  uploadBlueprint: async (file) => {
    const token = localStorage.getItem('sip_token');
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/blueprint/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (res.status === 402) {
      const body = await res.json().catch(() => ({}));
      throw new UpgradeRequiredError(body.detail || body);
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || body.message || `Blueprint upload failed: ${res.status}`);
    }
    return res.json();
  },
  deleteBlueprint: (blueprintId) =>
    request(`/blueprint/${blueprintId}`, { method: 'DELETE' }),

  // Last-N recent generations (for quick-reuse strip)
  recentGenerations: (section, limit = 5) => {
    const qs = new URLSearchParams();
    if (section) qs.set('section', section);
    qs.set('limit', String(limit));
    return request(`/recent-generations?${qs.toString()}`);
  },

  // Recurring weekly schedules (paid tiers only)
  listSchedules: () => request('/schedules'),
  createSchedule: (payload) => request('/schedules', { method: 'POST', body: JSON.stringify(payload) }),
  pauseSchedule: (id) => request(`/schedules/${id}/pause`, { method: 'POST' }),
  resumeSchedule: (id) => request(`/schedules/${id}/resume`, { method: 'POST' }),
  deleteSchedule: (id) => request(`/schedules/${id}`, { method: 'DELETE' }),

  // Subscription management (Auto-Renew + Cancel Anytime — Razorpay-ready)
  getMySubscription: () => request('/subscription'),
  toggleAutoRenew: (autoRenew) =>
    request('/subscription/auto-renew', { method: 'POST', body: JSON.stringify({ autoRenew }) }),
  cancelSubscription: () => request('/subscription/cancel', { method: 'POST' }),
  reactivateSubscription: () => request('/subscription/reactivate', { method: 'POST' }),

  // Authenticated downloads — fetches blob with Bearer token, then triggers browser download
  downloadAsset: async (format, assetId, fallbackFilename) => {
    const token = localStorage.getItem('sip_token');
    const res = await fetch(`${BASE}/download/${format}/${assetId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      const body = await res.text().catch(() => '');
      throw new Error(`Download failed (${res.status}): ${body.slice(0, 200)}`);
    }
    return _saveBlobFromResponse(res, fallbackFilename || `document.${format}`);
  },

  downloadTemplate: async (docType) => {
    const token = localStorage.getItem('sip_token');
    const res = await fetch(`${BASE}/templates/download/${docType}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Template download failed (${res.status})`);
    return _saveBlobFromResponse(res, `${docType}_template.docx`);
  },

  // Legacy URL helpers (kept for backward-compat; prefer the methods above)
  templateDownloadUrl: (docType) => `${BASE}/templates/download/${docType}`,
  assetDownloadUrl: (format, assetId) => `${BASE}/download/${format}/${assetId}`,

  // Access / subscription
  getAccess: () => request('/access'),

  // Library reader → generator
  readerImport: (payload) => request('/reader/import', { method: 'POST', body: JSON.stringify(payload) }),
  readerGutenbergBook: (gid) => request(`/reader/gutenberg/${gid}`),
  readerGutenbergChapter: (gid, n) => request(`/reader/gutenberg/${gid}/chapters/${n}`),

  // Insights
  computeInsights: (payload) =>
    request('/insights', { method: 'POST', body: JSON.stringify(payload) }),

  // Generation — Iter 73 — Fix #4: route via async polling to kill 504 timeouts.
  // Backwards-compatible signature: returns the resolved CEGenerationResponse.
  // Falls back to the sync `/generate` if the async path returns 404 (older API).
  generate: async (payload, { onProgress, signal, pollIntervalMs = 2500, maxWaitMs = 15 * 60 * 1000 } = {}) => {
    let start;
    try {
      start = await request('/generate/async', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
    } catch (err) {
      if (err.status === 404) {
        // Older backend without async wrapper — fall back to sync.
        return request('/generate', { method: 'POST', body: JSON.stringify(payload) });
      }
      throw err;
    }
    const jobId = start.jobId;
    if (!jobId) {
      // Backend returned a direct payload — pass through.
      return start;
    }
    const startedAt = Date.now();
    let lastStatus = start.status || 'queued';
    if (typeof onProgress === 'function') onProgress({ status: lastStatus, jobId, elapsedMs: 0 });
    while (true) {
      if (signal?.aborted) {
        const err = new Error('Generation cancelled');
        err.code = 'CANCELLED';
        throw err;
      }
      await new Promise((r) => setTimeout(r, pollIntervalMs));
      const job = await request(`/generate/job/${encodeURIComponent(jobId)}`);
      lastStatus = job.status;
      const elapsedMs = Date.now() - startedAt;
      if (typeof onProgress === 'function') onProgress({ status: lastStatus, jobId, elapsedMs });
      if (lastStatus === 'done') return job.result;
      if (lastStatus === 'failed') {
        const detail = job.error;
        if (job.errorCode === 402 && detail && typeof detail === 'object') {
          throw new UpgradeRequiredError(detail);
        }
        const msg = typeof detail === 'string' ? detail : detail?.message || 'Generation failed.';
        const e = new Error(msg);
        e.detail = detail;
        e.code = (detail && detail.code) || 'GENERATION_FAILED';
        throw e;
      }
      if (elapsedMs > maxWaitMs) {
        throw new Error(`Generation timed out after ${Math.round(maxWaitMs / 1000)} s.`);
      }
    }
  },
  // Legacy direct-sync (kept for tests / explicit non-polling callers).
  generateSync: (payload) =>
    request('/generate', { method: 'POST', body: JSON.stringify(payload) }),
  generateLessonKit: (payload) =>
    request('/lesson-kit/generate', { method: 'POST', body: JSON.stringify(payload) }),
  generatePPT: (payload) =>
    request('/ppt/generate', { method: 'POST', body: JSON.stringify(payload) }),

  // Assignments
  listClassSections: () => request('/classes-sections'),
  createAssignment: (payload) =>
    request('/assignments', { method: 'POST', body: JSON.stringify(payload) }),

  // Billing (Razorpay)
  listPlans: () => request('/billing/plans'),
  createSubscription: (planId, buyerState, business = {}) =>
    request('/billing/create-subscription', {
      method: 'POST',
      body: JSON.stringify({ planId, buyerState, buyerGstin: business.gstin || null, buyerBusinessName: business.name || null }),
    }),
  listReceipts: () => request('/billing/receipts'),
  downloadReceiptPdf: async (orderId, filename) => {
    const token = localStorage.getItem('sip_token');
    const res = await fetch(`${BASE}/billing/receipts/pdf?orderId=${encodeURIComponent(orderId)}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) throw new Error(`Download failed (${res.status})`);
    return _saveBlobFromResponse(res, filename);
  },
  previewPlanChange: (planId) => request(`/billing/preview-change?planId=${encodeURIComponent(planId)}`),
  verifyPayment: (payload) =>
    request('/billing/verify-payment', { method: 'POST', body: JSON.stringify(payload) }),
  cancelSubscription: () =>
    request('/billing/cancel', { method: 'POST' }),
  listAssignments: () => request('/assignments'),
  getAssignment: (id) => request(`/assignments/${id}`),

  // Evaluation & Improvements
  getEvaluation: (id) => request(`/assignments/${id}/evaluation`),
  getRecommendations: (id) => request(`/assignments/${id}/recommendations`),
  applyImprovements: (payload) =>
    request('/improvements/apply', { method: 'POST', body: JSON.stringify(payload) }),

  // Iteration (Micro AI actions)
  iterate: (payload) => request('/iterate', { method: 'POST', body: JSON.stringify(payload) }),

  // Teaching assets
  listAssets: (type) => request(`/assets${type ? `?type=${encodeURIComponent(type)}` : ''}`),
  getAsset: (id) => request(`/assets/${id}`),
  listLessonKits: () => request('/lesson-kits'),
  listTemplates: () => request('/templates'),
  popoverCounts: () => request('/assets/popover/counts'),

  // Teaching Co-Pilot (Pro+/School)
  copilotChat: (payload) =>
    request('/copilot/chat', { method: 'POST', body: JSON.stringify(payload) }),
  copilotUpload: async (file) => {
    const token = localStorage.getItem('sip_token');
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/copilot/upload`, {
      method: 'POST',
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      body: form,
    });
    if (res.status === 402) {
      const body = await res.json().catch(() => ({}));
      throw new UpgradeRequiredError(body.detail || body);
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || body.message || `Upload failed: ${res.status}`);
    }
    return res.json();
  },
  copilotExport: async ({ format, title, explanation, keyPoints, teachingTip }) => {
    const token = localStorage.getItem('sip_token');
    const res = await fetch(`${BASE}/copilot/export`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ format, title, explanation, keyPoints, teachingTip }),
    });
    if (res.status === 402) {
      const body = await res.json().catch(() => ({}));
      throw new UpgradeRequiredError(body.detail || body);
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.detail || body.message || `Export failed: ${res.status}`);
    }
    return _saveBlobFromResponse(res, `teaching-copilot.${format}`);
  },
};
